import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import './components/Root.css';
import './components/ScrollAnimation.css';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import TermsOfService from './pages/legal/TermsOfService';
import AdminApp from './admin/AdminApp';
import DocsApp from './DocsApp';
import HelpApp from './HelpApp';
import { PUBLIC_ROUTES } from './constants/routes';
import { isAdminSubdomain, isDocsSubdomain, isHelpSubdomain } from './utils/navigation';
import ProtectedDocsRoute from './components/routing/ProtectedDocsRoute';

// API Documentation pages
import ApiDocs from './pages/api-docs/ApiDocs';
import ApiDocsOverview from './pages/api-docs/pages/Overview';
import ApiDocsAuth from './pages/api-docs/pages/Authentication';
import ApiDocsSplits from './pages/api-docs/pages/Splits';
import ApiDocsTransactions from './pages/api-docs/pages/Transactions';
import ApiDocsWallets from './pages/api-docs/pages/Wallets';
import ApiDocsErrors from './pages/api-docs/pages/ErrorCodes';

// Help Center pages
import HelpCenter from './pages/help/HelpCenter';
import HelpGettingStarted from './pages/help/articles/getting-started';
import HelpFeatures from './pages/help/articles/features';
import HelpTroubleshooting from './pages/help/articles/troubleshooting';
import HelpAccount from './pages/help/articles/account';
import HelpIntegrations from './pages/help/articles/integrations';
import HelpCommunity from './pages/help/articles/community';
import HelpSupport from './pages/help/articles/support';
import HelpFAQ from './pages/help/articles/faq';
import HelpChangelog from './pages/help/articles/changelog';
// Individual article pages
import HelpWelcome from './pages/help/articles/getting-started/welcome';
import HelpFirstSplit from './pages/help/articles/getting-started/first-split';
import HelpAddingContacts from './pages/help/articles/getting-started/adding-contacts';
import HelpTopUpAccount from './pages/help/articles/getting-started/top-up-account';
import HelpCreateGroup from './pages/help/articles/getting-started/create-group';
import HelpManageGroup from './pages/help/articles/getting-started/manage-group';
import HelpTopUpGroup from './pages/help/articles/getting-started/top-up-group';
import HelpWithdrawGroup from './pages/help/articles/getting-started/withdraw-group';
import HelpLinkCardsWallets from './pages/help/articles/features/link-cards-wallets';
import HelpYourProfile from './pages/help/articles/features/your-profile';
import HelpBadgeCommunity from './pages/help/articles/features/badge-community';
import HelpSendP2P from './pages/help/articles/features/send-p2p';
import HelpRequestP2P from './pages/help/articles/features/request-p2p';
import HelpGasFees from './pages/help/articles/features/gas-fees';
import HelpRewards from './pages/help/articles/features/rewards';
import HelpGroups from './pages/help/articles/features/groups';
import HelpIntegration1 from './pages/help/articles/integrations/integration-1';
import HelpIntegration2 from './pages/help/articles/integrations/integration-2';
import HelpIntegration3 from './pages/help/articles/integrations/integration-3';
import HelpCommonIssues from './pages/help/articles/troubleshooting/common-issues';
import HelpPaymentProblems from './pages/help/articles/troubleshooting/payments';
import HelpAccountAccess from './pages/help/articles/troubleshooting/account-access';
import HelpProfile from './pages/help/articles/account/profile';
import HelpPrivacy from './pages/help/articles/account/privacy';
import HelpNotifications from './pages/help/articles/account/notifications';

function App() {
  const isAdmin = isAdminSubdomain();
  const isDocs = isDocsSubdomain();
  const isHelp = isHelpSubdomain();

  // If on admin subdomain, only show admin routes
  if (isAdmin) {
    return (
      <Router>
        <div className="App">
          <Routes>
            <Route path="/*" element={<AdminApp />} />
          </Routes>
        </div>
      </Router>
    );
  }

  // If on docs subdomain, only show API documentation routes
  if (isDocs) {
    return <DocsApp />;
  }

  // If on help subdomain, only show help center routes
  if (isHelp) {
    return <HelpApp />;
  }

  // Main website routes - landing page is root, docs accessible via /api-docs
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Landing page - root route */}
          <Route path={PUBLIC_ROUTES.HOME} element={<Home />} />
          <Route path={PUBLIC_ROUTES.TERMS} element={<Terms />} />
          <Route path="/Terms" element={<Terms />} />
          <Route path={PUBLIC_ROUTES.PRIVACY} element={<Privacy />} />
          <Route path="/Privacy" element={<Privacy />} />
          <Route path={PUBLIC_ROUTES.TERMS_OF_SERVICE} element={<TermsOfService />} />
          <Route path="/TermsOfService" element={<TermsOfService />} />
          
          {/* API Documentation routes - ALL PROTECTED */}
          <Route path={PUBLIC_ROUTES.API_DOCS} element={<ProtectedDocsRoute><ApiDocs /></ProtectedDocsRoute>} />
          <Route path={PUBLIC_ROUTES.API_DOCS_OVERVIEW} element={<ProtectedDocsRoute><ApiDocsOverview /></ProtectedDocsRoute>} />
          <Route path={PUBLIC_ROUTES.API_DOCS_AUTH} element={<ProtectedDocsRoute><ApiDocsAuth /></ProtectedDocsRoute>} />
          <Route path={PUBLIC_ROUTES.API_DOCS_SPLITS} element={<ProtectedDocsRoute><ApiDocsSplits /></ProtectedDocsRoute>} />
          <Route path={PUBLIC_ROUTES.API_DOCS_TRANSACTIONS} element={<ProtectedDocsRoute><ApiDocsTransactions /></ProtectedDocsRoute>} />
          <Route path={PUBLIC_ROUTES.API_DOCS_WALLETS} element={<ProtectedDocsRoute><ApiDocsWallets /></ProtectedDocsRoute>} />
          <Route path={PUBLIC_ROUTES.API_DOCS_ERRORS} element={<ProtectedDocsRoute><ApiDocsErrors /></ProtectedDocsRoute>} />
          
          {/* Alternative docs routes for easier localhost access */}
          <Route path="/docs" element={<Navigate to={PUBLIC_ROUTES.API_DOCS_OVERVIEW} replace />} />
          <Route path="/doc" element={<Navigate to={PUBLIC_ROUTES.API_DOCS_OVERVIEW} replace />} />
          <Route path="/docs/*" element={<Navigate to={PUBLIC_ROUTES.API_DOCS_OVERVIEW} replace />} />
          <Route path="/doc/*" element={<Navigate to={PUBLIC_ROUTES.API_DOCS_OVERVIEW} replace />} />
          
          {/* Help Center routes */}
          <Route path={PUBLIC_ROUTES.HELP} element={<HelpCenter />} />
          <Route path={PUBLIC_ROUTES.HELP_GETTING_STARTED} element={<HelpGettingStarted />} />
          <Route path={PUBLIC_ROUTES.HELP_FEATURES} element={<HelpFeatures />} />
          <Route path={PUBLIC_ROUTES.HELP_TROUBLESHOOTING} element={<HelpTroubleshooting />} />
          <Route path={PUBLIC_ROUTES.HELP_ACCOUNT} element={<HelpAccount />} />
          <Route path="/help/integrations" element={<HelpIntegrations />} />
          <Route path="/help/community" element={<HelpCommunity />} />
          <Route path="/help/support" element={<HelpSupport />} />
          <Route path="/help/faq" element={<HelpFAQ />} />
          <Route path="/help/changelog" element={<HelpChangelog />} />
          {/* Individual article routes - Getting Started */}
          <Route path="/help/getting-started/welcome" element={<HelpWelcome />} />
          <Route path="/help/getting-started/first-split" element={<HelpFirstSplit />} />
          <Route path="/help/getting-started/adding-contacts" element={<HelpAddingContacts />} />
          <Route path="/help/getting-started/top-up-account" element={<HelpTopUpAccount />} />
          <Route path="/help/getting-started/create-group" element={<HelpCreateGroup />} />
          <Route path="/help/getting-started/manage-group" element={<HelpManageGroup />} />
          <Route path="/help/getting-started/top-up-group" element={<HelpTopUpGroup />} />
          <Route path="/help/getting-started/withdraw-group" element={<HelpWithdrawGroup />} />
          {/* Individual article routes - Features */}
          <Route path="/help/features/link-cards-wallets" element={<HelpLinkCardsWallets />} />
          <Route path="/help/features/your-profile" element={<HelpYourProfile />} />
          <Route path="/help/features/badge-community" element={<HelpBadgeCommunity />} />
          <Route path="/help/features/send-p2p" element={<HelpSendP2P />} />
          <Route path="/help/features/request-p2p" element={<HelpRequestP2P />} />
          <Route path="/help/features/gas-fees" element={<HelpGasFees />} />
          <Route path="/help/features/rewards" element={<HelpRewards />} />
          <Route path="/help/features/groups" element={<HelpGroups />} />
          {/* Individual article routes - Integrations */}
          <Route path="/help/integrations/integration-1" element={<HelpIntegration1 />} />
          <Route path="/help/integrations/integration-2" element={<HelpIntegration2 />} />
          <Route path="/help/integrations/integration-3" element={<HelpIntegration3 />} />
          {/* Individual article routes - Troubleshooting */}
          <Route path="/help/troubleshooting/common-issues" element={<HelpCommonIssues />} />
          <Route path="/help/troubleshooting/payments" element={<HelpPaymentProblems />} />
          <Route path="/help/troubleshooting/account-access" element={<HelpAccountAccess />} />
          {/* Individual article routes - Account */}
          <Route path="/help/account/profile" element={<HelpProfile />} />
          <Route path="/help/account/privacy" element={<HelpPrivacy />} />
          <Route path="/help/account/notifications" element={<HelpNotifications />} />
          
          <Route path={PUBLIC_ROUTES.NOT_FOUND} element={<NotFound />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
